import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { getCountryCodes, getUserLocation } from "../services/publicApi";

const SELECTED_COUNTRY_KEY = "selectedCountry";
const LOCATION_DATA_KEY = "locationData";

const CountryContext = createContext(null);

export const CountryProvider = ({ children }) => {
  const [selectedCountry, setSelectedCountryState] = useState(() => {
    return sessionStorage.getItem(SELECTED_COUNTRY_KEY) || "";
  });
  const [teleCode, setTeleCode] = useState([]);

  const setSelectedCountry = useCallback((countryName) => {
    setSelectedCountryState(countryName);
    if (countryName) {
      sessionStorage.setItem(SELECTED_COUNTRY_KEY, countryName);
    } else {
      sessionStorage.removeItem(SELECTED_COUNTRY_KEY);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    const initializeCountry = async () => {
      try {
        const resTeleCode = await getCountryCodes();
        if (cancelled) return;

        if (resTeleCode.status !== 200 && resTeleCode.status !== 304) {
          return;
        }

        const countryCodes = resTeleCode.data?.data || [];
        setTeleCode(countryCodes);

        const savedCountry = sessionStorage.getItem(SELECTED_COUNTRY_KEY);
        if (savedCountry) {
          return;
        }

        const storedData = sessionStorage.getItem(LOCATION_DATA_KEY);
        if (storedData) {
          try {
            const { matchedCountry } = JSON.parse(storedData);
            if (matchedCountry?.name) {
              setSelectedCountry(matchedCountry.name);
            }
          } catch (parseError) {
            console.error("Error parsing locationData:", parseError);
          }
          return;
        }

        try {
          const { data } = await getUserLocation();
          if (cancelled) return;

          const userISO2 = data?.country;
          if (!userISO2 || !countryCodes.length) return;

          const matchedCountry = countryCodes.find((item) =>
            item.iso2?.toLowerCase().startsWith(String(userISO2).toLowerCase()),
          );

          if (matchedCountry) {
            setSelectedCountry(matchedCountry.name);
            sessionStorage.setItem(
              LOCATION_DATA_KEY,
              JSON.stringify({
                matchedCountry,
                matchedTimeZone: data?.timezone || null,
                ipTimezone: data?.timezone || null,
              }),
            );
          }
        } catch (locationError) {
          console.error("Error detecting country from IP:", locationError);
        }
      } catch (error) {
        console.error("Error initializing country list:", error);
      }
    };

    initializeCountry();

    return () => {
      cancelled = true;
    };
  }, [setSelectedCountry]);

  return (
    <CountryContext.Provider
      value={{
        selectedCountry,
        setSelectedCountry,
        teleCode,
        setTeleCode,
      }}
    >
      {children}
    </CountryContext.Provider>
  );
};

export const useCountry = () => {
  const context = useContext(CountryContext);
  if (!context) {
    throw new Error("useCountry must be used within a CountryProvider");
  }
  return context;
};
